import { t } from 'i18next';
import { useIsFetching, useMutation, useQuery } from 'react-query';

import { AttributeStatus } from 'companySettings/enums';
import { getPartFinderAttributes } from 'companySettings/services/partFinderAttribute';
import {
  comparePartFinderProductTypeVersions,
  getPartFinderAttributeNames,
  getPartFinderProductType,
  getPartFinderProductTypes,
  getPartFinderProductTypeVersions,
  publishPartFinderProductTypes,
  removePartFinderProductTypes,
  savePartFinderProductType,
} from 'companySettings/services/partFinderProductType';
import {
  IPartFinderAttribute,
  IPartFinderProductType,
  IPartFinderProductTypePayload,
} from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';

const PART_FINDER_PRODUCT_TYPES_QUERY_KEY = 'partFinderProductTypes';
const PART_FINDER_PRODUCT_TYPE_VERSIONS_QUERY_KEY = 'partFinderProductTypeVersions';
const PART_FINDER_ATTRIBUTE_NAMES_QUERY_KEY = 'partFinderAttributeNames';
const PUBLISHED_PART_FINDER_ATTRIBUTES_QUERY_KEY = 'publishedPartFinderAttributes';
const PUBLISHED_PART_FINDER_PRODUCT_TYPES_QUERY_KEY = 'publishedPartFinderProductTypes';

// The product type an editor opens, with its attributes and values. Under the grid's key, so a save
// that reloads the grid reloads it too.
const PART_FINDER_PRODUCT_TYPE_DETAIL_QUERY_KEY = [PART_FINDER_PRODUCT_TYPES_QUERY_KEY, 'detail'];

export const getPartFinderProductTypeQueryKey = (id: number | undefined) => [
  ...PART_FINDER_PRODUCT_TYPE_DETAIL_QUERY_KEY,
  id,
];

// True while any product type editor is loading its product type.
export const useIsPartFinderProductTypeLoading = () =>
  useIsFetching(PART_FINDER_PRODUCT_TYPE_DETAIL_QUERY_KEY) > 0;

export const useGetPartFinderProductType = (id: number | undefined, enabled: boolean) =>
  useQuery(
    getPartFinderProductTypeQueryKey(id),
    ({ signal }) => getPartFinderProductType(id, signal),
    { enabled: enabled && Boolean(id) }
  );

export const useGetPartFinderProductTypes = () =>
  useInfiniteQuery<IPartFinderProductType>(
    getPartFinderProductTypes,
    PART_FINDER_PRODUCT_TYPES_QUERY_KEY,
    {},
    { fetchAllPages: true }
  );

// The published product types with their released attributes, which a family inherits from.
export const useGetPublishedPartFinderProductTypes = () =>
  useInfiniteQuery<IPartFinderProductType>(
    getPartFinderProductTypes,
    PUBLISHED_PART_FINDER_PRODUCT_TYPES_QUERY_KEY,
    { filters: { published: 1 } },
    { fetchAllPages: true }
  );

// The published value lists with their released values, which an attribute can take its values
// from.
export const useGetPublishedPartFinderAttributes = (enabled: boolean) =>
  useInfiniteQuery<IPartFinderAttribute>(
    getPartFinderAttributes,
    PUBLISHED_PART_FINDER_ATTRIBUTES_QUERY_KEY,
    { filters: { published: 1 } },
    { fetchAllPages: true, enabled }
  );

export const useGetPartFinderAttributeNames = (enabled: boolean) =>
  useQuery(
    [PART_FINDER_ATTRIBUTE_NAMES_QUERY_KEY],
    ({ signal }) => getPartFinderAttributeNames(signal),
    { enabled }
  );

export const useGetPartFinderProductTypeVersions = (productTypeId: number, enabled: boolean) =>
  useQuery(
    [PART_FINDER_PRODUCT_TYPE_VERSIONS_QUERY_KEY, productTypeId],
    ({ signal }) => getPartFinderProductTypeVersions(productTypeId, signal),
    { enabled }
  );

export const useComparePartFinderProductTypeVersions = (
  fromVersionId: number,
  toVersionId: number,
  enabled: boolean
) =>
  useQuery(
    ['partFinderProductTypeComparison', fromVersionId, toVersionId],
    ({ signal }) => comparePartFinderProductTypeVersions({ fromVersionId, toVersionId }, signal),
    { enabled }
  );

export const usePartFinderProductTypeActions = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  return {
    ...useSavePartFinderProductType(position),
    ...useRemovePartFinderProductTypes(position),
    ...usePublishPartFinderProductTypes(position),
  };
};

const useSavePartFinderProductType = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (payload: IPartFinderProductTypePayload) => ({
      message:
        AttributeStatus.PUBLISHED === payload?.status
          ? t('messagePartFinderProductTypePublishSuccess')
          : t('messagePartFinderProductTypeSaveSuccess'),
    }),
    loading: () => ({
      message: t('messagePartFinderProductTypeSaveInProgress'),
    }),
    error: () => ({
      title: t('errorTitlePartFinderProductTypeSave'),
    }),
    position,
    // A publish adds a version to the history, and a save may have written values back to a value
    // list or used a new attribute name. A list write-back publishes the list, which syncs every
    // other product type on it (new revision and status), so the whole grid is reloaded.
    invalidateQueries: [
      PART_FINDER_PRODUCT_TYPES_QUERY_KEY,
      PART_FINDER_PRODUCT_TYPE_VERSIONS_QUERY_KEY,
      PART_FINDER_ATTRIBUTE_NAMES_QUERY_KEY,
      PUBLISHED_PART_FINDER_ATTRIBUTES_QUERY_KEY,
    ],
  });

  const { mutateAsync } = useMutation(savePartFinderProductType, {
    ...errors,
  });

  return { savePartFinderProductType: mutateAsync };
};

const useRemovePartFinderProductTypes = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (ids: number[]) => ({
      message: t('messagePartFinderProductTypesRemoveSuccess', { count: ids?.length }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderProductTypesRemoveInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderProductTypesRemove', { count: ids?.length }),
    }),
    position,
  });

  const { mutateAsync } = useMutation(removePartFinderProductTypes, {
    ...errors,
  });

  return { removePartFinderProductTypes: mutateAsync };
};

const usePublishPartFinderProductTypes = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    // The server leaves out drafts that matched their release, so its count is the exact one.
    success: (ids: number[], response?: { published?: number }) => ({
      message: t('messagePartFinderProductTypesPublishSuccess', {
        count: response?.published ?? ids?.length,
      }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderProductTypesPublishInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderProductTypesPublish', { count: ids?.length }),
    }),
    position,
    // Statuses and versions change on the rows, and each publish adds a version to the history.
    invalidateQueries: [
      PART_FINDER_PRODUCT_TYPES_QUERY_KEY,
      PART_FINDER_PRODUCT_TYPE_VERSIONS_QUERY_KEY,
    ],
  });

  const { mutateAsync } = useMutation(publishPartFinderProductTypes, {
    ...errors,
  });

  return { publishPartFinderProductTypes: mutateAsync };
};
